import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAsignarRol, useQuitarRol, useUsuarios } from '@/hooks/useUsuarios';
import { cn } from '@/lib/utils';
import { ROLES_APP, type RolApp } from '@adelante/shared';
import { Plus, Search, Trash2 } from 'lucide-react';
import { useState } from 'react';

/**
 * `/admin/usuarios` — gestión de roles (solo Admin).
 *
 * Vista por defecto: lista usuarios que YA tienen al menos un rol.
 * Buscador: filtra usuarios del tenant Entra ID por displayName/email para
 * asignar nuevos.
 *
 * El check de rol Admin es del backend (cada endpoint exige ROLES_ADMIN).
 * Acá no protegemos la ruta — si un Operador llega a la URL, las requests
 * fallan con 403 y la UI muestra el error.
 */

export function AdminUsuariosPantalla() {
  const [q, setQ] = useState('');
  const consulta = useUsuarios({ q: q.trim() || undefined });
  const asignar = useAsignarRol();
  const quitar = useQuitarRol();

  const usuarios = consulta.data?.data?.usuarios ?? [];
  const errorBackend = consulta.data?.error?.mensaje ?? null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Gestión de usuarios</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Asignación de roles (Admin / Operador / Laboratorio) a usuarios del tenant Adelante. Solo
          los usuarios con al menos un rol pueden acceder a la app.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Buscar usuario</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <div className="grid gap-1.5 max-w-md">
            <Label htmlFor="q">Nombre, correo o cuenta</Label>
            <div className="relative">
              <Search
                className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                id="q"
                placeholder="Dejá vacío para ver solo usuarios con roles asignados"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Si querés agregar a alguien que aún no tiene rol, buscalo por nombre o correo.
          </p>
        </CardContent>
      </Card>

      {errorBackend && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="p-4 text-sm text-destructive">{errorBackend}</CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Correo</TableHead>
                <TableHead>Roles actuales</TableHead>
                <TableHead className="w-[200px]">Asignar rol</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {consulta.isLoading &&
                ['s-a', 's-b', 's-c'].map((id) => (
                  <TableRow key={id}>
                    {['c1', 'c2', 'c3', 'c4'].map((c) => (
                      <TableCell key={`${id}-${c}`}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              {!consulta.isLoading && usuarios.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="py-10 text-center text-sm text-muted-foreground"
                  >
                    {q
                      ? `Sin resultados para "${q}".`
                      : 'No hay usuarios con roles asignados todavía. Buscá por nombre o correo para asignar el primero.'}
                  </TableCell>
                </TableRow>
              )}
              {!consulta.isLoading &&
                usuarios.map((u) => (
                  <TableRow key={u.oid}>
                    <TableCell className="font-medium">{u.nombre}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {u.roles.length === 0 && (
                          <span className="text-xs italic text-muted-foreground">sin roles</span>
                        )}
                        {u.roles.map((r) => (
                          <span
                            key={r.assignmentId}
                            className={cn(
                              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
                              r.rol === 'Admin' &&
                                'border-adelante-primary/40 bg-adelante-primary/10',
                            )}
                          >
                            {r.rol}
                            <button
                              type="button"
                              onClick={() => {
                                if (
                                  confirm(
                                    `¿Quitar rol "${r.rol}" a ${u.nombre}?\n\nSi era su único rol, va a perder acceso a la app.`,
                                  )
                                ) {
                                  quitar.mutate({
                                    userId: u.oid,
                                    assignmentId: r.assignmentId,
                                    rolLabel: r.rol,
                                  });
                                }
                              }}
                              disabled={quitar.isPending}
                              className="ml-0.5 rounded-full p-0.5 hover:bg-foreground/10"
                              aria-label={`Quitar rol ${r.rol}`}
                              title={`Quitar rol ${r.rol}`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <AsignarRolDropdown
                        rolesActuales={u.roles.map((r) => r.rol)}
                        cargando={asignar.isPending}
                        onAsignar={(rol) => asignar.mutate({ userId: u.oid, rol })}
                      />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// =============================================================================
// Dropdown inline para asignar rol nuevo a un usuario
// =============================================================================

function AsignarRolDropdown(props: {
  rolesActuales: RolApp[];
  cargando: boolean;
  onAsignar: (rol: RolApp) => void;
}) {
  const [seleccionado, setSeleccionado] = useState<RolApp | ''>('');
  const disponibles = ROLES_APP.filter((r) => !props.rolesActuales.includes(r));

  if (disponibles.length === 0) {
    return <span className="text-xs italic text-muted-foreground">tiene todos</span>;
  }

  return (
    <div className="flex items-center gap-1">
      <Select
        value={seleccionado}
        onValueChange={(v) => setSeleccionado(v as RolApp)}
        disabled={props.cargando}
      >
        <SelectTrigger className="h-8 w-[130px] text-xs">
          <SelectValue placeholder="Elegir rol…" />
        </SelectTrigger>
        <SelectContent>
          {disponibles.map((r) => (
            <SelectItem key={r} value={r}>
              {r}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        size="icon"
        variant="outline"
        className="h-8 w-8"
        disabled={!seleccionado || props.cargando}
        onClick={() => {
          if (seleccionado) {
            props.onAsignar(seleccionado);
            setSeleccionado('');
          }
        }}
        title="Asignar rol seleccionado"
        aria-label="Asignar rol"
      >
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  );
}
